// Chronomètre du terrain (D-098), repris de Playbook BCL : jusqu'à 4 chronos indépendants, comptes à
// rebours ou chronomètre qui monte (ajout d'Arceau), préréglages qui arment ET lancent le chrono actif.
// Chaque chrono se cale sur l'horloge (instant de départ + reste figé à la pause) : un téléphone mis en
// veille retombe juste. Logique pure, horloge injectée ; l'écran vit dans ui/chrono.ts.

export interface Preset { label: string; seconds: number }
/** Préréglages basket par défaut (Playbook : 24", 14", 8", 5", 1' ; Arceau ajoute le match de 8'). */
export const DEFAULT_PRESETS: Preset[] = [
  { label: 'Possession', seconds: 24 }, { label: 'Rebond off.', seconds: 14 }, { label: 'Remise en jeu', seconds: 8 },
  { label: 'Sortie de balle', seconds: 5 }, { label: 'Temps-mort', seconds: 60 }, { label: 'Match', seconds: 480 },
];
export const PRESETS_MAX = 15;
export const CHRONO_MAX = 4;

/** Le score : de 2 à 10 équipes, chacune son nom et sa couleur, comme Playbook (« min 2, max 10 »). */
export const TEAMS_MIN = 2;
export const TEAMS_MAX = 10;
/** Les couleurs des équipes nommées, celles de Playbook (le gris ardoise, invisible sur le fond sombre, devient jaune). */
export const TEAM_COLORS = ['#2f6df6', '#e23b3b', '#27ae60', '#f39c12', '#9b59b6', '#16a085', '#e84393', '#facc15', '#d35400', '#1abc9c'];
export const DEFAULT_NAMES = ['Domicile', 'Extérieur'];
/** Le nom d'une équipe laissée sans nom : Domicile, Extérieur, puis « Équipe 3 »… */
export const teamName = (i: number) => DEFAULT_NAMES[i] ?? `Équipe ${i + 1}`;
const HEX = /^#[0-9a-f]{6}$/i;

export interface ScoreTeam { name: string; color: string }
export interface TeamChrono { presets: Preset[]; score: { mode: 'bibs' | 'names'; teams: ScoreTeam[] } }

/** Le réglage de l'équipe (teams.chrono), relu sans confiance ; vide : les défauts. Un réglage d'avant (deux noms, sans
 *  couleurs) se lit tel quel : bleu et rouge, comme avant. */
export function readTeamChrono(raw: unknown): TeamChrono {
  const r = (raw && typeof raw === 'object' ? raw : {}) as { presets?: unknown; score?: { mode?: unknown; names?: unknown; colors?: unknown } };
  const presets = cleanPresets(r.presets);
  const names = Array.isArray(r.score?.names) ? r.score!.names.slice(0, TEAMS_MAX).map(x => (typeof x === 'string' ? x.trim().slice(0, 20) : '')) : [];
  const colors = Array.isArray(r.score?.colors) ? r.score!.colors : [];
  const teams = Array.from({ length: Math.max(TEAMS_MIN, names.length) }, (_, i) => {
    const c = colors[i];
    return { name: names[i] || teamName(i), color: typeof c === 'string' && HEX.test(c) ? c.toLowerCase() : TEAM_COLORS[i % TEAM_COLORS.length]! };
  });
  return {
    presets: presets.length ? presets : DEFAULT_PRESETS.map(p => ({ ...p })),
    score: { mode: r.score?.mode === 'names' ? 'names' : 'bibs', teams },
  };
}
/** Ce qui part en base (team_set_chrono) : les noms et leurs couleurs côte à côte (`names` reste lu par les anciennes versions). */
export function storedChrono(c: TeamChrono) {
  return { presets: c.presets, score: { mode: c.score.mode, names: c.score.teams.map(t => t.name), colors: c.score.teams.map(t => t.color) } };
}
/** Une équipe de plus (10 au plus) : « Équipe 3 », avec la première couleur pas encore prise (comme Playbook). */
export function addScoreTeam(teams: ScoreTeam[]): ScoreTeam[] {
  if (teams.length >= TEAMS_MAX) return teams;
  const used = new Set(teams.map(t => t.color.toLowerCase())), names = new Set(teams.map(t => t.name));
  let k = teams.length;
  while (names.has(teamName(k))) k++;
  return [...teams, { name: teamName(k), color: TEAM_COLORS.find(c => !used.has(c)) ?? TEAM_COLORS[teams.length % TEAM_COLORS.length]! }];
}
/** Retire l'équipe i ; jamais sous deux (un score à une équipe n'a pas de sens). */
export const removeScoreTeam = (teams: ScoreTeam[], i: number) => (teams.length <= TEAMS_MIN ? teams : teams.filter((_, k) => k !== i));
/** Les équipes du score : les chasubles de l'exercice (deux au moins, dix au plus), sinon les noms de l'équipe. */
export function scoreTeams(c: TeamChrono, bibs?: ScoreTeam[]): ScoreTeam[] {
  return c.score.mode === 'bibs' && (bibs?.length ?? 0) >= TEAMS_MIN ? bibs!.slice(0, TEAMS_MAX) : c.score.teams;
}

export function cleanPresets(raw: unknown): Preset[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(p => ({ label: String((p as Preset)?.label ?? '').trim().slice(0, 24), seconds: Math.round(Number((p as Preset)?.seconds)) }))
    .filter(p => Number.isFinite(p.seconds) && p.seconds >= 1 && p.seconds <= 5400).slice(0, PRESETS_MAX);
}

/** La tuile d'un préréglage : 24", 1', 1:30, 8'. */
export function chipMain(seconds: number): string {
  if (seconds < 60) return `${seconds}"`;
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return s ? `${m}:${String(s).padStart(2, '0')}` : `${m}'`;
}

// --- Un chrono ------------------------------------------------------------------------------------
export interface Timer {
  id: string;
  label: string;
  /** Compte à rebours, ou chronomètre qui monte. */
  kind: 'down' | 'up';
  totalMs: number;
  /** Reste (décompte) ou temps écoulé (chronomètre) au dernier arrêt. */
  baseMs: number;
  /** Instant du départ en cours ; null : à l'arrêt. */
  startedAt: number | null;
  done: boolean;
}

export const isRunning = (t: Timer) => t.startedAt !== null;
export function newTimer(id: string, kind: 'down' | 'up', preset?: Preset): Timer {
  const ms = kind === 'down' ? (preset?.seconds ?? 24) * 1000 : 0;
  return { id, label: preset?.label ?? (kind === 'up' ? 'Chronomètre' : 'Chrono'), kind, totalMs: ms, baseMs: ms, startedAt: null, done: false };
}
/** La valeur affichée : le reste (décompte), le temps écoulé (chronomètre). */
export function valueMs(t: Timer, now: number): number {
  if (t.startedAt === null) return t.baseMs;
  const run = now - t.startedAt;
  return t.kind === 'down' ? Math.max(0, t.baseMs - run) : t.baseMs + run;
}
/** Départ (ou reprise) ; un décompte fini repart de sa durée. */
export function start(t: Timer, now: number): Timer {
  if (t.startedAt !== null) return t;
  const fresh = t.kind === 'down' && (t.done || t.baseMs <= 0);
  return { ...t, baseMs: fresh ? t.totalMs : t.baseMs, startedAt: now, done: false };
}
export function pause(t: Timer, now: number): Timer {
  return t.startedAt === null ? t : { ...t, baseMs: valueMs(t, now), startedAt: null };
}
export const toggle = (t: Timer, now: number) => (isRunning(t) ? pause(t, now) : start(t, now));
export function reset(t: Timer): Timer {
  return { ...t, baseMs: t.kind === 'down' ? t.totalMs : 0, startedAt: null, done: false };
}
/** Un préréglage arme ET lance : le chrono devient un décompte de cette durée. */
export function arm(t: Timer, p: Preset, now: number): Timer {
  const ms = p.seconds * 1000;
  return { ...t, kind: 'down', label: p.label || chipMain(p.seconds), totalMs: ms, baseMs: ms, startedAt: now, done: false };
}
/** À chaque tic : un décompte arrivé à zéro s'arrête, `fired` dit qu'il faut sonner. */
export function tick(t: Timer, now: number): { timer: Timer; fired: boolean } {
  if (t.kind !== 'down' || t.startedAt === null || valueMs(t, now) > 0) return { timer: t, fired: false };
  return { timer: { ...t, baseMs: 0, startedAt: null, done: true }, fired: true };
}
/** L'état, pour la couleur : en cours, alerte (5 dernières secondes), fini, à l'arrêt. */
export function tone(t: Timer, now: number): 'run' | 'warn' | 'done' | 'idle' {
  if (t.done) return 'done';
  if (t.startedAt === null) return 'idle';
  return t.kind === 'down' && valueMs(t, now) <= 5000 ? 'warn' : 'run';
}
export function stateLabel(t: Timer): string {
  if (t.done) return 'terminé';
  if (t.startedAt !== null) return '● en cours';
  const fresh = t.kind === 'down' ? t.baseMs === t.totalMs : t.baseMs === 0;
  return fresh ? 'prêt' : '⏸ en pause';
}

// --- Formats --------------------------------------------------------------------------------------
/** Décompte : MM:SS au-dessus de 10 s (secondes entamées : 24 s dès l'armement), dixièmes en dessous. */
export function fmtCountdown(ms: number): string {
  const v = Math.max(0, ms);
  if (v < 10_000) return (Math.floor(v / 100) / 10).toFixed(1);
  const s = Math.ceil(v / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
/** Chronomètre : m:ss.d (« 1:12.6 »). */
export function fmtStopwatch(ms: number): string {
  const d = Math.floor(Math.max(0, ms) / 100);
  const s = Math.floor(d / 10);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}.${d % 10}`;
}
export const fmtTimer = (t: Timer, now: number) => (t.kind === 'down' ? fmtCountdown(valueMs(t, now)) : fmtStopwatch(valueMs(t, now)));

/** Tic-tac 3-2-1 : une note montante par seconde (600, 800, 1100 Hz), sinon rien. */
export const tensionFreq = (secLeft: number) => (secLeft === 3 ? 600 : secLeft === 2 ? 800 : secLeft === 1 ? 1100 : null);

/** Score : jamais sous zéro. */
export const addPoints = (score: number, delta: number) => Math.max(0, score + delta);
